// src/pages/RequestAccess.jsx
// Opened from the "Request access" button in the email sent to people who
// fax by email without a Fax Portal account (…/portal/?request-access=<token>).
// They give their name; the group's Admins are emailed to approve it.
import { useEffect, useState } from 'react';
import { trackPage } from '../utils/visits';
import { checkAccessLink, requestAccess } from '../utils/api';
import { pathForTab, DEFAULT_TAB } from '../utils/tabRoutes';
import AuthShell, { A, AuthMessage } from '../components/AuthShell';

export function accessTokenFromLocation() {
  return new URLSearchParams(window.location.search).get('request-access');
}

export default function RequestAccess({ token }) {
  useEffect(() => { trackPage('request-access'); }, []);   // 3.49.0: Analytics (visitors before signing in)
  // state: 'checking' | 'form' | 'sending' | 'pending' | 'has_access' | 'problem'
  const [state, setState]     = useState('checking');
  const [email, setEmail]     = useState('');
  const [name, setName]       = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    checkAccessLink(token)
      .then(r => {
        if (!r.valid) { setMessage(r.message); setState('problem'); return; }
        setEmail(r.email); setName(r.name || '');
        setState(r.status === 'pending' ? 'pending' : r.status === 'has_access' ? 'has_access' : 'form');
      })
      .catch(e => { setMessage(e.message); setState('problem'); });
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setState('sending'); setMessage('');
    try {
      const r = await requestAccess(token, name.trim());
      setState(r.status === 'has_access' ? 'has_access' : 'pending');
    } catch (e2) { setMessage(e2.message); setState('form'); }
  }

  const goToSignIn = () => window.location.assign(pathForTab(DEFAULT_TAB));

  return (
    <AuthShell stack>
      <h1 style={S.title}>Request a Fax Portal account</h1>

      {state === 'checking' && <p style={S.text}>Checking your link…</p>}

      {(state === 'form' || state === 'sending') && (
        <form onSubmit={submit} style={S.form}>
          <p style={S.text}>
            You're asking for an account for <strong>{email}</strong>. Your team's Admins will be emailed, and you'll get an email when it's ready.
          </p>
          <label style={S.label} htmlFor="ra-name">Your name</label>
          <input id="ra-name" value={name} onChange={e => setName(e.target.value)} required maxLength={80}
            autoFocus autoComplete="name" placeholder="First and last name" style={S.input} />
          {message && <AuthMessage tone="error">{message}</AuthMessage>}
          <button type="submit" disabled={state === 'sending' || !name.trim()} className="auth-btn">
            {state === 'sending' ? 'Sending…' : 'Send request'}
          </button>
        </form>
      )}

      {state === 'pending' && (
        <AuthMessage tone="success">
          Your request was sent. Your team's Admins will set up your account, and you'll get an email with how to sign in.
          Until then, faxes you send by email aren't sent. Once your account is ready, send them again.
        </AuthMessage>
      )}

      {state === 'has_access' && (
        <>
          <AuthMessage tone="success">You already have a Fax Portal account. Sign in with Google, or use "Forgot password?" to set a password.</AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to sign in</button>
        </>
      )}

      {state === 'problem' && (
        <>
          <AuthMessage tone="error">{message}</AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to the Fax Portal</button>
        </>
      )}
    </AuthShell>
  );
}


const S = {  // 3.40.0: the page and card come from AuthShell; the rest follows its look (A)
  title: { ...A.title, margin: 0 },
  text: { ...A.text, margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, ...A.label },
  input: { ...A.input, marginBottom: 0 },
};
